<template>
    <div v-if="modelValue" class="scoring-overlay" @click.self="onCancel">
        <div class="scoring-dialog">
            <div class="scoring-header">
                <h3 class="scoring-title">{{ $t("Kpi.task_scoring") }}</h3>
                <button class="scoring-close" @click="onCancel">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5"
                            stroke-linecap="round" />
                    </svg>
                </button>
            </div>

            <div class="scoring-section">
                <div class="scoring-label-row">
                    <span class="scoring-label">{{ $t("Kpi.time_score") }}</span>
                    <span class="scoring-value" :class="timeScoreColorClass"> {{ timeScore }}/{{ MAX_TIME_SCORE }}
                    </span>
                </div>
                <div class="scoring-progress-track">
                    <div class="scoring-progress-fill" :class="timeScoreColorClass"
                        :style="{ width: timeScorePercent + '%' }" />
                </div>
                <span class="scoring-hint">{{ $t("Kpi.auto_calculated_time") }}</span>
            </div>

            <div class="scoring-section" v-if="!isQualityLocked">
                <div class="scoring-label-row">
                    <span class="scoring-label">{{ $t("Kpi.quality_score") }}</span>
                    <span class="scoring-value score-green"> {{ qualityScore }}/{{ MAX_QUALITY_SCORE }} </span>
                </div>
                <div class="scoring-slider-wrapper">
                    <input type="range" class="scoring-slider" :min="0" :max="MAX_QUALITY_SCORE" :step="1"
                        v-model.number="qualityScore" :style="{
                            background: `linear-gradient(to right,
                        #FC0 0%,
                        #FF8D28 ${qualityScorePercent}%,
                        #34C759 ${qualityScorePercent}%,
                        #e5e5e5 ${qualityScorePercent}%,
                        #e5e5e5 100%)`
                        }" />
                    <div class="scoring-slider-labels">
                        <span v-for="n in qualityScoreMarks" :key="n">{{ n }}</span>
                    </div>
                </div>
                <span v-if="qualityScoreReason" class="scoring-hint scoring-reason">
                    {{ qualityScoreReason }}
                </span>
            </div>

            <div class="scoring-section" v-else>
                <div class="scoring-label-row">
                    <span class="scoring-label">{{ $t("Kpi.quality_score") }}</span>
                    <span class="scoring-value score-red">0/{{ MAX_QUALITY_SCORE }}</span>
                </div>
            </div>

            <Transition name="bonus-slide">
                <div v-if="showBonus" class="scoring-section">
                    <div class="scoring-label-row">
                        <span class="scoring-label">{{ $t("Kpi.bonus_score") }}</span>
                        <span class="scoring-value">+{{ bonusScore }}</span>
                    </div>
                    <div class="scoring-slider-wrapper">
                        <input type="range" class="scoring-slider-bonus scoring-slider--bonus" :min="0"
                            :max="MAX_BONUS_SCORE" :step="1" v-model.number="bonusScore" :style="{
                                background: `
      linear-gradient(90deg, var(--Mint-900, #00544B) 0%, var(--Mint-700, #008E7F) 50%, var(--Mint-500, #00C8B3) 100%)
      0 / ${bonusScorePercent}% 100% no-repeat,
      #e5e7eb
    `
                            }" />
                    </div>
                </div>
            </Transition>

            <div class="scoring-section">
                <span class="scoring-label">{{ $t("Kpi.comment") }}</span>
                <textarea class="scoring-textarea" v-model="comment"
                    :placeholder="$t('Kpi.feedback_placeholder')" rows="4" />
            </div>

            <div class="scoring-total-card">
                <span class="scoring-total-label">{{ $t("Kpi.total_score") }}</span>
                <div class="scoring-total-value">
                    <span class="scoring-total-number" :class="totalScoreColorClass">{{ totalScore }}/</span>
                    <span class="scoring-total-max">{{ MAX_TOTAL_SCORE }}</span>
                </div>
            </div>

            <div class="scoring-actions">
                <button class="scoring-btn scoring-btn--cancel" @click="onCancel">{{ $t("Common.cancel") }}</button>
                <button class="scoring-btn scoring-btn--submit" :disabled="isSubmitting"
                    @click="isShowConfirmModal = true">
                    {{ isSubmitting ? $t("Kpi.submitting") : $t("Kpi.submit") }}
                </button>
            </div>
        </div>
    </div>

    <ConfirmScoringModal v-model="isShowConfirmModal" :loading="submitLoading" @confirm="submitScoring" />
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import ConfirmScoringModal from "../ConfirmScoringModal/ConfirmScoringModal.vue";

const { t } = useI18n();

const MAX_TIME_SCORE = 45;
const MAX_QUALITY_SCORE = 45;
const MAX_BONUS_SCORE = 10;
const MAX_TOTAL_SCORE = 100;

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    task: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(["update:modelValue", "submit", "cancel"]);

// ─── State ───
const qualityScore = ref(0);
const comment = ref("");
const bonusScore = ref(0);
const isSubmitting = ref(false);
const isShowConfirmModal = ref(false);

// ===== REOPEN / REJECT RULE =====
const rejectCount = computed(() => {
    return Number(props.task?.rejectCount || props.task?.reopenCount || 0);
});

const isQualityLocked = computed(() => rejectCount.value >= 5);

const allowedQualityScore = computed(() => {
    if (rejectCount.value && rejectCount.value >= 5) return 0;

    return MAX_QUALITY_SCORE - rejectCount.value * 5;
});
const qualityScoreMarks = computed(() => {
    const marks = [];
    for (let i = 0; i <= MAX_QUALITY_SCORE; i += 5) {
        marks.push(i);
    }
    return marks;
});
const qualityScoreReason = computed(() => {
    if (rejectCount.value >= 5) {
        return t("Kpi.quality_locked_reason", { count: rejectCount.value });
    }

    if (rejectCount.value > 0) {
        return t("Kpi.quality_reopen_reason", { score: allowedQualityScore.value, max: MAX_QUALITY_SCORE });
    }

    return null;
});
const bonusScorePercent = computed(() => {
    return (bonusScore.value / MAX_BONUS_SCORE) * 100;
});
watch(
    [() => props.modelValue, rejectCount],
    ([isOpen]) => {
        if (isOpen) {
            if (isQualityLocked.value) {
                qualityScore.value = 0;
            } else {
                qualityScore.value = Math.min(0, allowedQualityScore.value);
            }

            bonusScore.value = 0;
            comment.value = "";
            isSubmitting.value = false;
        }
    },
    { immediate: true },
);
const qualityScorePercent = computed(() => {
    return (qualityScore.value / MAX_QUALITY_SCORE) * 100;
});
// nếu user cố kéo vượt rule thì auto kéo về mức hợp lệ
watch(qualityScore, (val) => {
    if (isQualityLocked.value) {
        qualityScore.value = 0;
        return;
    }

    if (val > allowedQualityScore.value) {
        qualityScore.value = allowedQualityScore.value;
    }

    if (val < 0) {
        qualityScore.value = 0;
    }
});

const timeScore = computed(() => {
    return calculateTimeScore(props.task);
});

function calculateTimeScore(task) {
    const DEDUCTION_PER_DAY = 5;
    const OVERDUE_ZERO_SCORE_DAYS = 5;
    if (task.dateChangeCount >= 5) return 0;

    const dueDate = parseDateField(task?.DueDate);
    if (!dueDate) return MAX_TIME_SCORE;

    const today = new Date();

    const dueDay = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
    const currentDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const diffMs = currentDay.getTime() - dueDay.getTime();
    const daysLate = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (daysLate <= 0) return MAX_TIME_SCORE;

    if (daysLate >= OVERDUE_ZERO_SCORE_DAYS) return 0;

    const deduction = daysLate * DEDUCTION_PER_DAY;
    return Math.max(0, MAX_TIME_SCORE - deduction);
}

function parseDateField(dateField) {
    if (!dateField) return null;
    if (dateField instanceof Date) return dateField;
    if (dateField?.seconds) return new Date(dateField.seconds * 1000);
    if (typeof dateField === "number") {
        return dateField > 1e12 ? new Date(dateField) : new Date(dateField * 1000);
    }
    if (typeof dateField === "string") return new Date(dateField);
    return null;
}

const totalScore = computed(() => timeScore.value + qualityScore.value + bonusScore.value);

const timeScorePercent = computed(() => (timeScore.value / MAX_TIME_SCORE) * 100);

const timeScoreColorClass = computed(() => {
    if (timeScore.value >= 40) return "score-green";
    if (timeScore.value >= 20) return "score-orange";
    return "score-red";
});

const totalScoreColorClass = computed(() => {
    if (totalScore.value >= 70) return "score-green";
    if (totalScore.value >= 40) return "score-orange";
    return "score-red";
});

const showBonus = computed(() => {
    return !isQualityLocked.value && timeScore.value === MAX_TIME_SCORE && qualityScore.value === MAX_QUALITY_SCORE;
});

function onCancel() {
    emit("cancel");
    emit("update:modelValue", false);
}

async function submitScoring() {
    const timePercent = (timeScore.value / MAX_TIME_SCORE) * 100;
    const qualityPercent = (qualityScore.value / MAX_QUALITY_SCORE) * 100;
    const bonusPercent = (bonusScore.value / MAX_BONUS_SCORE) * 100;

    const scoringData = {
        timeScore: timeScore.value,
        timePercent,
        qualityScore: qualityScore.value,
        qualityPercent,
        bonusScore: bonusScore.value,
        bonusPercent,
        totalScore: totalScore.value,
        comment: comment.value,
        rejectCount: rejectCount.value,
    };

    isShowConfirmModal.value = false;
    emit("submit", scoringData);
    emit("update:modelValue", false);
}
</script>

<style scoped src="./style.css"></style>
